"use client";

import React from "react";
import {
  EnhancedTabs,
  EnhancedTabsList,
  EnhancedTabsTrigger,
} from "@/components/ui/tabs/enhanced-tabs";
import { AlertTriangle } from "lucide-react";
import { Form } from "@/components/ui/form";
import { ComposeFormFields } from "./ComposeFormFields";
import { ComposeFormActions } from "./ComposeFormActions";
import { ContextVariablesList } from "../variables/components/ContextVariablesList";
import { VariableCounter } from "../variables/components/VariableCounter";
import { ComposeHistoryItem } from "@/lib/utils/crm/compose-history";
import { ComposeHistoryPanel } from "./ComposeHistoryPanel";
import { useComposeRecipientsContext } from "@/lib/contexts/compose/ComposeRecipientsContext";
import { useComposeClientContext } from "@/lib/contexts/compose/ComposeClientContext";
import { useComposeFormStateContext } from "@/lib/contexts/compose/ComposeFormStateContext";
import {
  hasTemplateVariables,
  replaceTemplateVariables,
  mapContactToTemplateVariables,
} from "@/lib/utils/crm/template-variables";
import {
  ComposeFormValues,
  ComposeTab,
  EmailConnection,
  ComposeDisclosureOptions,
} from "@/lib/types/compose-modal-types";
import type { Message } from "@/lib/core/crm/types";
import { EmailTemplate } from "@/lib/hooks/api/emails/email-templates";
import type { FieldErrors } from "react-hook-form";

interface ComposeFormTabsProps {
  globalModalId?: string;
  onSubmit: (values: ComposeFormValues) => void;
  onCancel: () => void;
  onGoToFullScreen: () => void;
  /** Show the "Full Screen" hand-off button. Hidden on inline surfaces. */
  showFullScreenButton?: boolean;
  onCreateTask?: () => void;
  onCreateTicket?: () => void;
  originalMessage?: Message | null;
  isSending?: boolean;
  emailConnections?: EmailConnection[];
  onRestoreFromHistory?: (item: ComposeHistoryItem) => void;
  onClientSelect?: (clientId: string) => void;
  onTemplateSelect?: (template: EmailTemplate) => void;
  /** Progressive-disclosure config for narrow/inline surfaces. */
  disclosure?: ComposeDisclosureOptions;
}

export function ComposeFormTabs({
  onSubmit,
  onCancel,
  onGoToFullScreen,
  showFullScreenButton = true,
  onCreateTask,
  onCreateTicket,
  originalMessage,
  isSending,
  emailConnections,
  onRestoreFromHistory,
  onClientSelect,
  onTemplateSelect,
  disclosure,
}: ComposeFormTabsProps) {
  const hideHistoryTab = disclosure?.hideHistoryTab ?? false;
  const formState = useComposeFormStateContext();
  const recipientsState = useComposeRecipientsContext();
  const clientState = useComposeClientContext();

  const {
    form,
    activeTab,
    onTabChange,
    hasUnfilledVariables,
    variableValues,
  } = formState;
  const watchedFromAccount = form.watch("fromAccount");
  const selectedFromEmail = emailConnections?.find(
    (connection) => connection.connectionId === watchedFromAccount
  )?.email;

  const {
    recipients,
    onRecipientsChange,
    setCcRecipients,
    setBccRecipients,
  } = recipientsState;

  const { selectedClient } = clientState;
  const handleSubmitWithValidation = (values: ComposeFormValues) => {
    if (hasUnfilledVariables && Object.keys(variableValues).length === 0) {
      // Show variables tab instead of sending
      onTabChange("variables");
      return;
    }

    onSubmit(values);
  };

  const handleValidationError = (errors: FieldErrors<ComposeFormValues>) => {
    // Ensure we're on the compose tab to see the errors
    onTabChange("compose");

    // Scroll to first error field for better UX
    const firstErrorField = Object.keys(errors)[0];
    if (firstErrorField) {
      // Wait a tick for the tab to switch before scrolling
      setTimeout(() => {
        const errorElement = document.querySelector(`[name="${firstErrorField}"]`);
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 0);
    }
  };

  const handleTabChangeLocal = (tab: string) => {
    onTabChange(tab as ComposeTab);
  };

  const handleHistoryRestore = (item: ComposeHistoryItem) => {
    onTabChange("compose");
    onRestoreFromHistory?.(item);
  };

  return (
    <EnhancedTabs
      value={activeTab}
      onValueChange={handleTabChangeLocal}
      className="flex-1 flex flex-col"
    >
      <EnhancedTabsList
        className={`grid w-full ${hideHistoryTab ? "grid-cols-2" : "grid-cols-3"}`}
      >
        <EnhancedTabsTrigger
          value="compose"
          variant="blue"
          className="flex items-center justify-center gap-2"
        >
          Compose
          {hasUnfilledVariables && activeTab !== "compose" && (
            <AlertTriangle className="h-4 w-4 text-amber-500" />
          )}
        </EnhancedTabsTrigger>
        <EnhancedTabsTrigger
          value="variables"
          variant="green"
          className="flex items-center justify-center gap-2"
        >
          Variables
          <VariableCounter />
        </EnhancedTabsTrigger>
        {!hideHistoryTab && (
          <EnhancedTabsTrigger
            value="history"
            variant="purple"
            className="flex items-center justify-center"
          >
            History
          </EnhancedTabsTrigger>
        )}
      </EnhancedTabsList>

      <div className="flex-1 mt-4 overflow-y-auto">
        {activeTab === "compose" && (
          <div>
            <Form {...form}>
              <form
                onSubmit={async (e) => {
                  // Prevent form submission from propagating to parent drawers
                  e.preventDefault();
                  e.stopPropagation();

                  // Sync "to" from recipients when form field is empty but user has selected recipients
                  // (avoids ZodError when selector state and form get out of sync)
                  let currentTo = (form.getValues("to") || "").trim();
                  if (!currentTo && recipients.length > 0) {
                    currentTo = recipients.map((r) => r.email).join(", ");
                    form.setValue("to", currentTo);
                  }
                  // Resolve template variables in "to" so validation sees actual emails
                  if (currentTo && hasTemplateVariables(currentTo)) {
                    const vars = {
                      ...(selectedClient ? mapContactToTemplateVariables(selectedClient) : {}),
                      ...variableValues,
                    } as Record<string, string>;
                    const resolvedTo = replaceTemplateVariables(currentTo, vars, false);
                    if (resolvedTo.trim()) form.setValue("to", resolvedTo.trim());
                  }

                  // Manually trigger validation
                  const isValid = await form.trigger();

                  if (!isValid) {
                    handleValidationError(form.formState.errors);
                    return;
                  }

                  // If validation passed, submit
                  const values = form.getValues();
                  handleSubmitWithValidation(values);
                }}
                className="space-y-4 pb-4"
              >
                <ComposeFormFields
                  form={form}
                  originalMessage={originalMessage}
                  emailConnections={emailConnections}
                  onTemplateSelect={onTemplateSelect}
                />
                <ComposeFormActions
                  hasUnfilledVariables={hasUnfilledVariables}
                  onGoToFullScreen={onGoToFullScreen}
                  showFullScreenButton={showFullScreenButton}
                  onCancel={onCancel}
                  onCreateTask={onCreateTask}
                  onCreateTicket={onCreateTicket}
                  originalMessage={originalMessage}
                  isSending={isSending}
                  sendFromEmail={selectedFromEmail || undefined}
                />
              </form>
            </Form>
          </div>
        )}

        {activeTab === "variables" && (
          <div className="p-4">
            <ContextVariablesList />
          </div>
        )}

        {activeTab === "history" && (
          <div className="p-4">
            <ComposeHistoryPanel
              form={form}
              onClientSelect={onClientSelect ?? (() => {})}
              onRecipientsChange={onRecipientsChange}
              setCcRecipients={setCcRecipients}
              setBccRecipients={setBccRecipients}
              onRestoreFromHistory={handleHistoryRestore}
              isVisible={true}
            />
          </div>
        )}
      </div>
    </EnhancedTabs>
  );
}
